import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/components/common/Button/Button';
import Popup from '@/shared/components/layers/Popup/Popup';
import type { DepartmentInterface } from '@/shared/model/api.types';
import { historyError, salesHistoryApi } from './api';
import { SnapshotComparisonResult } from './SnapshotComparison';
import type { SalesPlanComparison } from './types';
import { formatDate, money } from './utils';
import css from './PlanHistory.module.css';

const signedMoney = (value: number) => `${value > 0 ? '+' : ''}${money(value)}`;
const dealsLabel = (count: number) => {
  const mod100 = count % 100;
  const mod10 = count % 10;
  const word = mod100 >= 11 && mod100 <= 14 ? 'сделок' : mod10 === 1 ? 'сделка' : mod10 >= 2 && mod10 <= 4 ? 'сделки' : 'сделок';
  return `${count} ${word}`;
};
const dealChangesLabel = (count: number) => {
  const singular = count % 10 === 1 && count % 100 !== 11;
  return `${dealsLabel(count)} ${singular ? 'изменилась' : 'изменились'}`;
};

function periodLabel(item: SalesPlanComparison) {
  return `${formatDate(item.dateStart)} — ${formatDate(item.dateEnd)}`;
}

export default function SnapshotAnalytics({ departments, onClose }: {
  departments: DepartmentInterface[];
  onClose: () => void;
}) {
  const [departmentId, setDepartmentId] = useState('');
  const [items, setItems] = useState<SalesPlanComparison[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setLoading(true); setError(''); setExpandedId(null);
    salesHistoryApi.analytics(departmentId ? Number(departmentId) : undefined, request.signal)
      .then(result => { if (!request.signal.aborted) setItems(result); })
      .catch(requestError => { if (!request.signal.aborted) { setItems([]); setError(historyError(requestError)); } })
      .finally(() => { if (!request.signal.aborted) setLoading(false); });
    return () => request.abort();
  }, [departmentId, revision]);

  const changedSnapshots = items.filter(item => item.hasDifferences).length;
  const changedDeals = items.reduce((sum, item) => sum + item.deals.length, 0);

  return <Popup open onClose={onClose} size="xl" fullHeight>
    <div className={css.dialog} role="dialog" aria-modal="true" aria-labelledby="snapshot-analytics-title">
      <div className={css.heading}>
        <div>
          <h2 id="snapshot-analytics-title">Аналитика срезов</h2>
          <p>Сравнение всех сохранённых срезов с текущим состоянием CRM до конкретной сделки.</p>
        </div>
        <Button variant="secondary" size="s" onClick={onClose}>Закрыть</Button>
      </div>

      <div className={css.analyticsToolbar}>
        <label className={css.field}>Отдел
          <select value={departmentId} onChange={event => setDepartmentId(event.target.value)}>
            <option value="">Все доступные отделы</option>
            {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
          </select>
        </label>
        <Button variant="secondary" size="s" disabled={loading} onClick={() => setRevision(value => value + 1)}>Обновить сравнение</Button>
      </div>

      {loading && <p role="status">Сравниваем сохранённые срезы с текущими сделками…</p>}
      {error && <p role="alert" className={css.error}>{error}</p>}
      {!loading && !error && items.length === 0 && <p className={css.notice}>Сохранённых срезов пока нет.</p>}
      {!loading && !error && items.length > 0 && <>
        <div className={css.analyticsTotals}>
          <div><span>Всего срезов</span><strong>{items.length}</strong></div>
          <div><span>С расхождениями</span><strong className={changedSnapshots ? css.negativeText : css.positiveText}>{changedSnapshots}</strong></div>
          <div><span>Изменившихся сделок</span><strong className={changedDeals ? css.negativeText : css.positiveText}>{changedDeals}</strong></div>
        </div>

        <div className={css.snapshotList}>{items.map(item => {
          const isExpanded = expandedId === item.snapshot.id;
          return <article key={item.snapshot.id} className={`${css.snapshotCard} ${item.hasDifferences ? css.snapshotChanged : css.snapshotClean}`}>
            <div className={css.snapshotCardHeader}>
              <div>
                <div className={css.snapshotIdentity}>
                  <strong>Срез №{item.snapshot.id}</strong>
                  <span>{item.departmentName}</span>
                  <span>{periodLabel(item)}</span>
                </div>
                <small>Зафиксирован {new Date(item.snapshot.createdAt).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })} (МСК)</small>
              </div>
              <span className={`${css.snapshotStatus} ${item.hasDifferences ? css.statusChanged : css.statusClean}`}>
                {item.hasDifferences ? (item.deals.length ? dealChangesLabel(item.deals.length) : 'Изменились итоговые показатели') : 'Без изменений'}
              </span>
            </div>

            <div className={css.snapshotMetrics}>
              <div><span>Оборот</span><strong>{money(item.opportunity.snapshot.total)} → {money(item.opportunity.current.total)}</strong><small className={item.opportunity.delta.total ? css.changedValue : undefined}>{signedMoney(item.opportunity.delta.total)}</small></div>
              <div><span>Валовая прибыль</span><strong>{money(item.profit.snapshot.total)} → {money(item.profit.current.total)}</strong><small className={item.profit.delta.total ? css.changedValue : undefined}>{signedMoney(item.profit.delta.total)}</small></div>
              <div><span>Оплачено</span><strong>{money(item.paid.snapshot)} → {money(item.paid.current)}</strong><small className={item.paid.delta ? css.changedValue : undefined}>{signedMoney(item.paid.delta)}</small></div>
            </div>

            <Button variant="secondary" size="s" onClick={() => setExpandedId(isExpanded ? null : item.snapshot.id)}>
              {isExpanded ? 'Скрыть детализацию' : item.hasDifferences ? 'Показать изменившиеся сделки' : 'Показать проверку'}
            </Button>
            {isExpanded && <div className={css.snapshotDetails}><SnapshotComparisonResult comparison={item}/></div>}
          </article>;
        })}</div>
      </>}
    </div>
  </Popup>;
}
